import { Stack, Typography } from '@mui/material';
import { TypographyWithOverflowHint } from '@schooly/style';

import useSchoolYears from 'apps/web/src/hooks/useSchoolYears';
import { FC, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { SelectContentSkeleton } from '../SelectContentSkeleton';

type SchoolYearSelectProps = {
  selectedId: string;
  onSelect: (v: string) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const SchoolYearSelect: FC<SchoolYearSelectProps> = ({
  selectedId,
  onSelect,
  ...dropdownProps
}) => {
  const { schoolYears, isLoading } = useSchoolYears();
  const dropdown = useRef<DropdownSelect | null>(null);

  const selectedYear = schoolYears.find((y) => y.id === selectedId);

  const renderLabel = useCallback((name: string, isSelected: boolean) => {
    return (
      <TypographyWithOverflowHint
        color={!isSelected ? 'common.grey2' : undefined}
        variant="h3"
        noWrap
      >
        {name}
      </TypographyWithOverflowHint>
    );
  }, []);

  const renderContent = useCallback(() => {
    if (isLoading) return <SelectContentSkeleton />;

    if (!schoolYears.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack gap={0.25} p={1}>
        {schoolYears.map((y) => {
          const isSelected = selectedId === y.id;
          return (
            <DropdownSelectRow
              key={y.id}
              onClick={() => {
                onSelect(y.id);
                dropdown.current?.close();
              }}
              isSelected={isSelected}
            >
              {renderLabel(y.name, isSelected)}
            </DropdownSelectRow>
          );
        })}
      </Stack>
    );
  }, [isLoading, onSelect, renderLabel, schoolYears, selectedId]);

  return (
    <DropdownSelect
      ref={dropdown}
      hasValues={!!selectedId}
      renderContent={renderContent}
      {...dropdownProps}
    >
      {() => <Typography variant="h3">{selectedYear?.name}</Typography>}
    </DropdownSelect>
  );
};
